import { useMemo, useState } from 'react';

import { Check, ChevronsUpDown } from 'lucide-react';

import { Badge } from '@/shadcn/badge';
import { Button } from '@/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/shadcn/command';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';

import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import type { LinkQueryResult } from '../traceInspector.types';

type LinkOption = {
  linkHash: string;
  label: string;
  icon?: string;
  height: number;
};

type Props = {
  links: LinkQueryResult[];
  selectedLinkHash: string | null;
  onSelectLink: (linkHash: string) => void;
  label: string;
  disabled?: boolean;
  isLinkDisabled?: (linkHash: string) => boolean;
};

export const LinkSelector = ({
  links,
  selectedLinkHash,
  onSelectLink,
  label,
  disabled = false,
  isLinkDisabled
}: Props) => {
  const [open, setOpen] = useState(false);

  // Convert links to options with display format: [Icon] Action Title [n]
  // where [n] is a badge showing the height
  const linkOptions = useMemo<LinkOption[]>(() => {
    // Sort by height DESC (newest first)
    const sortedLinks = [...links].sort((a, b) => b.height - a.height);

    return sortedLinks.map(link => ({
      linkHash: link.linkHash,
      label: link.action?.title || 'Untitled',
      icon: link.action?.icon,
      height: link.height
    }));
  }, [links]);

  const selectedLink = useMemo(
    () => linkOptions.find(option => option.linkHash === selectedLinkHash),
    [linkOptions, selectedLinkHash]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="font-medium text-sm">{label}</div>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            aria-expanded={open}
            className={cn(
              'h-10 w-full justify-between border-input font-normal text-foreground text-sm normal-case active:scale-100 disabled:cursor-not-allowed',
              !selectedLink && 'text-foreground/60'
            )}
            disabled={disabled}
            role="combobox"
            variant="outline"
          >
            {selectedLink ? (
              <div className="flex items-center gap-2">
                {selectedLink.icon && (
                  <DynamicIcon icon={selectedLink.icon} size={16} />
                )}
                <span>{selectedLink.label}</span>
                <Badge className="px-2" variant="outline">
                  {selectedLink.height}
                </Badge>
              </div>
            ) : (
              'Select a link'
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command>
            <CommandInput placeholder="Search links..." />
            <CommandList>
              <CommandEmpty>No links found.</CommandEmpty>
              <CommandGroup>
                {linkOptions.map(linkOption => {
                  const isDisabled =
                    isLinkDisabled?.(linkOption.linkHash) ?? false;
                  return (
                    <CommandItem
                      disabled={isDisabled}
                      key={linkOption.linkHash}
                      onSelect={() => {
                        if (!isDisabled) {
                          onSelectLink(linkOption.linkHash);
                          setOpen(false);
                        }
                      }}
                      value={linkOption.linkHash}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedLinkHash === linkOption.linkHash
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      <div
                        className={cn(
                          'flex flex-1 items-center gap-2',
                          isDisabled && 'opacity-50'
                        )}
                      >
                        <Badge className="mr-2 px-2" variant="outline">
                          {linkOption.height}
                        </Badge>
                        {linkOption.icon && (
                          <DynamicIcon icon={linkOption.icon} size={16} />
                        )}
                        <span className="flex-1">{linkOption.label}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
